import { getDevtools } from '@/modules/devtools'
import type { LoggerEvent } from '@/types/logger.types'
import { timer } from '@/utils/helpers'
import { loadScript } from '@/utils/loadScript'

import { AdConsoleLogger } from '../../utils/ad-console-logger'
import { generateAdElementId } from '../../utils/generate-ad-id'
import {
    type ApsAdUnit,
    type ApsBidContext,
    type ApsOptions,
    type Apstag,
    type ApsTargeting,
    APS_TARGETING_KEYS,
} from './types'

const APSTAG_SOURCE = 'https://c.amazon-adsystem.com/aax2/apstag.js'
const BID_TIMEOUT_MS = 2_000

export class Aps {
    private readonly apstag: Promise<Apstag | undefined>

    private readonly adConsoleLogger = new AdConsoleLogger('aps')

    constructor(private readonly options: ApsOptions) {
        this.apstag = loadScript({ src: APSTAG_SOURCE, scriptName: 'APS', useExisting: true }).then((result) => {
            if (result.status !== 'ok' || !window.apstag) {
                this.adConsoleLogger.error('apstag is not available', result)
                return undefined
            }

            window.apstag.init({ pubID: options.pubID, adServer: 'googletag' })
            return window.apstag
        })
    }

    async fetchBids(adUnit: ApsAdUnit, context: ApsBidContext): Promise<ApsTargeting | undefined> {
        const startedAt = Date.now()
        const apstag = await Promise.race([this.apstag, timer(BID_TIMEOUT_MS)])
        const sizes = getBannerSizes(adUnit)
        // apstag replaces a non-positive timeout with its own 2s default
        const timeout = BID_TIMEOUT_MS - (Date.now() - startedAt)
        if (!apstag || !sizes.length || timeout <= 0) {
            return undefined
        }

        const slotID = context.slotElementId || generateAdElementId()
        const { promise, resolve } = Promise.withResolvers<ApsTargeting | undefined>()

        apstag.fetchBids({ slots: [{ slotID, slotName: adUnit.code, sizes }], timeout }, (bids, meta) => {
            const bid = bids.find((candidate) => candidate.slotID === slotID)
            const outcome = getOutcome(bid?.amzniid, meta?.fromTimeout)

            this.adConsoleLogger.debug(`${outcome} for ${context.placement} (${context.provider})`, bid)
            const event: LoggerEvent = {
                event: 'custom_event',
                eventName: 'ad_aps_response',
                adPlacement: context.placement,
                adProvider: context.provider,
                adUnit: adUnit.code,
                dimension3: outcome,
            }
            if (context.adRequestId !== undefined) {
                getDevtools()?.markAdRequest(event, context.adRequestId)
            }
            this.options.logEvent(event)

            resolve(
                bid && outcome === 'bid'
                    ? Object.fromEntries(APS_TARGETING_KEYS.map((key) => [key, bid[key]]).filter(([, value]) => value))
                    : undefined,
            )
        })

        return promise
    }
}

function getBannerSizes(adUnit: ApsAdUnit): [number, number][] {
    const sizes = adUnit.mediaTypes?.banner?.sizes || []
    return sizes.filter((size): size is [number, number] => Array.isArray(size) && size[0] > 1)
}

function getOutcome(bidId: string | undefined, fromTimeout: boolean | undefined): 'timeout' | 'bid' | 'no_bid' {
    if (bidId) {
        return 'bid'
    }
    return fromTimeout ? 'timeout' : 'no_bid'
}
